import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { ArrowLeft, CreditCard, Shield } from 'lucide-react';
import { initiateParkingPayment, submitPaymentForm } from '../../services/adumoService';
import {
  calculateTicketPricing,
  formatCurrency,
  formatDuration,
  formatNamibianDateTime
} from '../../utils/parkingTicketUtils';

const PageContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #7f1b1c 0%, #640003 100%);
  padding: 120px 20px 80px;
`;

const Card = styled.div`
  max-width: 600px;
  margin: 0 auto;
  background: #ffffff;
  border-radius: 16px;
  padding: 32px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.25);
`;

const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: none;
  background: transparent;
  color: #64748b;
  font-weight: 600;
  cursor: pointer;
  margin-bottom: 16px;
  padding: 0;
`;

const Title = styled.h1`
  margin: 0 0 8px;
  color: #1e293b;
  text-align: center;
`;

const Subtitle = styled.p`
  margin: 0 0 24px;
  color: #64748b;
  text-align: center;
`;

const Summary = styled.div`
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 24px;
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 16px;
  padding: 6px 0;
  color: #334155;
  font-size: 0.92rem;

  strong {
    color: #1e293b;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  color: #334155;
  font-size: 0.875rem;
  font-weight: 600;
  margin-bottom: 8px;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 1rem;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #c31321;
    box-shadow: 0 0 0 3px rgba(195, 19, 33, 0.1);
  }
`;

const AmountBox = styled.div`
  padding: 14px 16px;
  border-radius: 8px;
  background: #fff7ed;
  border: 1px solid #fed7aa;
  color: #9a3412;
  font-weight: 700;
  font-size: 1.1rem;
`;

const SubmitButton = styled.button`
  width: 100%;
  border: none;
  border-radius: 8px;
  padding: 14px;
  background: #c31321;
  color: #fff;
  font-size: 1rem;
  font-weight: 700;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const ErrorText = styled.p`
  color: #b91c1c;
  font-weight: 600;
  font-size: 0.9rem;
`;

const InfoText = styled.p`
  margin: 16px 0 0;
  color: #64748b;
  font-size: 0.85rem;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const ParkingCardPayment = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const parkingPayment = location.state?.parkingPayment;
  const ticket = parkingPayment?.ticket;
  const plateKey = parkingPayment?.plateKey || '';
  const pricing = ticket
    ? calculateTicketPricing(ticket.attributes || {}, plateKey)
    : parkingPayment?.pricing;
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!pricing || !ticket) {
    return (
      <PageContainer>
        <Card>
          <Title>Payment unavailable</Title>
          <Subtitle>Please return to Open Tickets and select Pay again.</Subtitle>
          <BackButton type="button" onClick={() => navigate('/smart-parking/open-tickets')}>
            <ArrowLeft size={16} />
            Back to Open Tickets
          </BackButton>
        </Card>
      </PageContainer>
    );
  }

  const validateEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');

    if (!validateEmail(email)) {
      setError('Please enter a valid email address.');
      return;
    }

    setIsSubmitting(true);

    try {
      const attributes = ticket.attributes || {};
      const response = await initiateParkingPayment({
        amount: pricing.totalPayable,
        currency: pricing.currency,
        email,
        eventId: pricing.eventId,
        orderNumber: pricing.orderNumber,
        pan: pricing.pan || attributes.pan
      });

      submitPaymentForm(response.paymentLink, response.paymentParameters);
    } catch (submitError) {
      setError(submitError.message || 'Failed to start card payment. Please try again.');
      setIsSubmitting(false);
    }
  };

  return (
    <PageContainer>
      <Card>
        <BackButton
          type="button"
          onClick={() => navigate('/smart-parking/open-tickets/pay', { state: { ticket } })}
        >
          <ArrowLeft size={16} />
          Back to payment methods
        </BackButton>

        <Title>Pay Parking with Card</Title>
        <Subtitle>Complete your KPI parking payment securely online.</Subtitle>

        <Summary>
          <SummaryRow>
            <span>Vehicle plate</span>
            <strong>{pricing.vehiclePlate}</strong>
          </SummaryRow>
          <SummaryRow>
            <span>Duration</span>
            <strong>{formatDuration(pricing.billableHours)}</strong>
          </SummaryRow>
          <SummaryRow>
            <span>Stay</span>
            <strong>
              {formatNamibianDateTime(pricing.startDate)} – {formatNamibianDateTime(pricing.endDate)}
            </strong>
          </SummaryRow>
        </Summary>

        <form onSubmit={handleSubmit}>
          <FormGroup>
            <Label>Email address</Label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your.email@example.com"
              required
            />
          </FormGroup>

          <FormGroup>
            <Label>Amount due</Label>
            <AmountBox>{formatCurrency(pricing.totalPayable, pricing.currency)}</AmountBox>
          </FormGroup>

          {error && <ErrorText>{error}</ErrorText>}

          <SubmitButton type="submit" disabled={isSubmitting}>
            <CreditCard size={18} />
            {isSubmitting ? 'Redirecting to payment...' : 'Proceed to Payment'}
          </SubmitButton>
        </form>

        <InfoText>
          <Shield size={16} />
          Payments are processed through secure 3D Secure authentication.
        </InfoText>
      </Card>
    </PageContainer>
  );
};

export default ParkingCardPayment;
